import { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  IconButton,
  Collapse,
  Divider,
  Paper,
  Stack,
  Badge,
  Pagination,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  SubdirectoryArrowRight as ReplyArrowIcon,
  Verified as VerifiedIcon,
  SupervisorAccount as AdminIcon,
} from "@mui/icons-material";
import { BoardCommentListItemDto } from "@/dto/BoardCommentDto";
import { useCommentList } from "./useCommentList";

interface CommentProps {
  boardId: string;
  admIdntfcId?: string;
  isSuperRootUser?: boolean;
}

export const Comment = ({
  boardId,
  admIdntfcId,
  isSuperRootUser = false,
}: CommentProps) => {
  const theme = useTheme();
  const commentInputRef = useRef<HTMLInputElement>(null);

  const {
    parentComments,
    childCommentMap,
    loading,
    error,
    commentCount,
    expanded,
    replyToId,
    editingCommentId,
    page,
    limit,
    commentText,
    handleDeleteComment,
    handlePageChange,
    toggleExpanded,
    setReplyMode,
    setEditMode,
    refreshComments,
    formatDate,
    handleCommentChange,
    handleCancel,
    handleSubmitComment,
    isSuperRoot,
  } = useCommentList({ boardId, admIdntfcId, isSuperRootUser });

  // 답글 작성 모드 설정 시 해당 댓글의 내용을 입력창에 넣지 않음
  useEffect(() => {
    if (replyToId && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [replyToId]);

  // 수정 모드 설정 시 자동 포커스
  useEffect(() => {
    if (editingCommentId && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [editingCommentId]);

  // 대댓글 렌더링 함수
  const renderChildComment = (
    childComment: BoardCommentListItemDto,
    parentComment: BoardCommentListItemDto
  ) => {
    const isCommentFromSuperRoot = isSuperRoot(childComment.admIdntfcId);

    return (
      <Paper
        key={childComment.commentId}
        elevation={0}
        sx={{
          p: 2,
          mb: 1,
          backgroundColor: isCommentFromSuperRoot
            ? alpha(theme.palette.primary.light, 0.1)
            : alpha(theme.palette.background.paper, 0.5),
          borderRadius: 1,
          borderLeft: `4px solid ${
            isCommentFromSuperRoot
              ? theme.palette.error.main
              : theme.palette.primary.light
          }`,
        }}
      >
        {/* 대댓글 헤더 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: isCommentFromSuperRoot
                  ? theme.palette.error.main
                  : theme.palette.secondary.main,
                fontSize: "0.75rem",
              }}
            >
              {isCommentFromSuperRoot ? (
                <AdminIcon fontSize="small" />
              ) : (
                childComment.admName.charAt(0)
              )}
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color={isCommentFromSuperRoot ? "error" : "inherit"}
                >
                  {childComment.admName}
                </Typography>
                {isCommentFromSuperRoot && (
                  <Chip
                    icon={<VerifiedIcon fontSize="small" />}
                    label="슈퍼 관리자"
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{
                      height: "20px",
                      "& .MuiChip-icon": { fontSize: "0.85rem" },
                      "& .MuiChip-label": { fontSize: "0.7rem", py: 0.2 },
                    }}
                  />
                )}
                <Chip
                  icon={<ReplyArrowIcon fontSize="small" />}
                  label={`@${parentComment.admName}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    height: "20px",
                    "& .MuiChip-icon": { fontSize: "0.85rem" },
                    "& .MuiChip-label": { fontSize: "0.7rem", py: 0.2 },
                  }}
                />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
              >
                {formatDate(childComment.regDttm)}
              </Typography>
            </Box>
          </Stack>

          {/* 대댓글 액션 버튼 */}
          {admIdntfcId === childComment.admIdntfcId && (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="수정">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditMode(childComment.commentId);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="삭제">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteComment(childComment.commentId);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        {/* 대댓글 내용 */}
        <Typography
          variant="body2"
          component="div"
          sx={{
            py: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {childComment.commentContent}
        </Typography>
      </Paper>
    );
  };

  // 댓글 렌더링 함수
  const renderComment = (comment: BoardCommentListItemDto) => {
    const isCommentFromSuperRoot = isSuperRoot(comment.admIdntfcId);

    return (
      <Box key={comment.commentId} sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: isCommentFromSuperRoot
              ? alpha(theme.palette.primary.light, 0.1)
              : alpha(theme.palette.background.default, 0.7),
            borderRadius: 1,
            border: isCommentFromSuperRoot
              ? `1px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* 댓글 헤더 */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isCommentFromSuperRoot
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                  fontSize: "0.875rem",
                }}
              >
                {isCommentFromSuperRoot ? (
                  <AdminIcon />
                ) : (
                  comment.admName.charAt(0)
                )}
              </Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color={isCommentFromSuperRoot ? "error" : "inherit"}
                  >
                    {comment.admName}
                  </Typography>
                  {isCommentFromSuperRoot && (
                    <Chip
                      icon={<VerifiedIcon fontSize="small" />}
                      label="슈퍼 관리자"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{
                        height: "20px",
                        "& .MuiChip-icon": { fontSize: "0.85rem" },
                        "& .MuiChip-label": { fontSize: "0.7rem", py: 0.2 },
                      }}
                    />
                  )}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {formatDate(comment.regDttm)}
                </Typography>
              </Box>
            </Stack>

            {/* 댓글 액션 버튼 */}
            {admIdntfcId && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="답글 작성">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReplyMode(comment.commentId);
                    }}
                  >
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {admIdntfcId === comment.admIdntfcId && (
                  <>
                    <Tooltip title="수정">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditMode(comment.commentId);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteComment(comment.commentId);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            )}
          </Box>

          {/* 댓글 내용 */}
          <Typography
            variant="body2"
            component="div"
            sx={{
              py: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {comment.commentContent}
          </Typography>
        </Paper>

        {/* 대댓글 섹션 */}
        {childCommentMap[comment.commentId] &&
          childCommentMap[comment.commentId].length > 0 && (
            <Box sx={{ pl: 4, mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 0.5,
                  ml: 1,
                }}
              >
                <ReplyArrowIcon
                  fontSize="small"
                  color="action"
                  sx={{ mr: 0.5, fontSize: "1rem" }}
                />
                <Typography variant="caption" color="text.secondary">
                  답글 {childCommentMap[comment.commentId].length}개
                </Typography>
              </Box>
              {childCommentMap[comment.commentId].map((childComment) =>
                renderChildComment(childComment, comment)
              )}
            </Box>
          )}

        {/* 답글 작성 영역 */}
        {replyToId === comment.commentId && (
          <Box sx={{ pl: 4, mt: 1 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                borderRadius: 1,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <ReplyArrowIcon
                  fontSize="small"
                  color="primary"
                  sx={{ mr: 0.5 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  <b>{comment.admName}</b>님에게 답글 작성
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                size="small"
                placeholder="답글을 입력하세요..."
                value={commentText}
                onChange={handleCommentChange}
                inputRef={commentInputRef}
                InputProps={{
                  endAdornment: (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={handleCancel}
                        color="default"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleSubmitComment}
                        disabled={!commentText.trim() || loading}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ),
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          cursor: "pointer",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={toggleExpanded}
        >
          <Badge
            badgeContent={commentCount}
            color="primary"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                height: "20px",
                minWidth: "20px",
              },
            }}
          >
            <ChatIcon color="action" />
          </Badge>
          <Typography variant="subtitle1" fontWeight={600}>
            댓글
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {expanded && (
            <Tooltip title="새로고침">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  refreshComments();
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={toggleExpanded}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {/* 댓글 목록 */}
        {!loading && parentComments.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            {parentComments.map(renderComment)}

            {/* 페이지네이션 */}
            {commentCount > limit && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  mb: 1,
                }}
              >
                <Pagination
                  count={Math.ceil(commentCount / limit)}
                  page={page}
                  onChange={(_, value) => handlePageChange(value)}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </Box>
        ) : (
          !loading && (
            <Box
              sx={{
                py: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                등록된 댓글이 없습니다.
              </Typography>
            </Box>
          )
        )}

        <Divider sx={{ my: 2 }} />

        {/* 댓글 작성 영역 - 대댓글 작성 모드에서는 표시하지 않음 */}
        {admIdntfcId && !replyToId && (
          <Box>
            {editingCommentId && (
              <Typography
                variant="caption"
                color="primary"
                component="div"
                sx={{ mb: 1 }}
              >
                댓글 수정 중...
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder={
                editingCommentId
                  ? "댓글을 수정하세요..."
                  : "댓글을 입력하세요..."
              }
              value={commentText}
              onChange={handleCommentChange}
              inputRef={commentInputRef}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 1,
                gap: 1,
              }}
            >
              {editingCommentId && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancel}
                  size="small"
                  startIcon={<CancelIcon />}
                >
                  취소
                </Button>
              )}
              <Button
                variant="contained"
                disableElevation
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || loading}
                endIcon={<SendIcon />}
                size="small"
              >
                {editingCommentId ? "수정" : "등록"}
              </Button>
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
